/**
 * @file: useTaskHeaderOptions.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { useCancelOrder } from '@euler/app/flows/record/functions/useCancelOrder';
import { useTaskNavigation } from '@euler/app/flows/task/functions/useTaskNavigation';
import { useSharePdf } from '@euler/functions/sharing/useSharePdf';
import { useShareReport } from '@euler/functions/sharing/useShareReport';
import { TaskBasicInfo } from '@euler/model/task-detail';
import { useServiceFactory } from '@euler/services/factory';
import { usePersistFn } from '@euler/utils/hooks';
import { useActionSheet } from '@expo/react-native-action-sheet';
import invariant from 'tiny-invariant';

type ActionType =
  | 'wechat-share-to-customer'
  | 'wechat-share-to-sa'
  | 'wecom-share-to-customer'
  | 'wecom-share-to-sa'
  | 'sms-share-to-customer'
  | 'print-order'
  | 'refresh'
  | 'cancel-order'
  | 'cancel'
  | 'create-child-order';

type ActionItem = { type: ActionType; text: string; destructive?: boolean };

export const useTaskHeaderOptions = ({
  basicInfo,
  shareToken,
  canCreateChildOrder,
  onRefresh,
}: {
  basicInfo: TaskBasicInfo;
  shareToken?: string;
  canCreateChildOrder: boolean;
  onRefresh?: () => void;
}) => {
  const appNav = useAppNavigation();
  const taskNav = useTaskNavigation();
  const { showActionSheetWithOptions } = useActionSheet();

  const {
    isWeixinSharingAvailable,
    isWeWorkSharingAvailable,
    shareReportViaWeixin,
    shareReportViaWeWork,
    shareReportViaSms,
  } = useShareReport();

  const {
    taskNo,
    orderNo,
    reportNo,
    licensePlateNo,
    customerMobile,
    isMainOrder,
  } = basicInfo;

  const { canCancelOrder, cancelOrder } = useCancelOrder({
    orderNo,
    taskNo,
    isMainOrder,
  });

  const { orderService } = useServiceFactory();
  const sharePdf = useSharePdf(
    encodeURIComponent(`${licensePlateNo}-接车单.pdf`),
  );

  const showActions = usePersistFn(() => {
    const items: ActionItem[] = [];
    if (isWeixinSharingAvailable) {
      items.push(
        { type: 'wechat-share-to-customer', text: '微信分享报告给车主' },
        { type: 'wechat-share-to-sa', text: '微信分享报告给服务顾问' },
      );
    }
    if (isWeWorkSharingAvailable) {
      items.push(
        { type: 'wecom-share-to-customer', text: '企业微信分享报告给车主' },
        { type: 'wecom-share-to-sa', text: '企业微信分享报告给服务顾问' },
      );
    }
    items.push(
      { type: 'sms-share-to-customer', text: '短信分享报告给车主' },
      { type: 'print-order', text: '打印接车单' },
      { type: 'refresh', text: '刷新工单信息' },
    );

    if (canCancelOrder) {
      items.push({
        type: 'cancel-order',
        text: '关闭/取消当前订单',
        destructive: true,
      });
    }
    if (isMainOrder && canCreateChildOrder) {
      items.push({
        type: 'create-child-order',
        text: '新建子订单',
      });
    }

    items.push({ type: 'cancel', text: '返回' });

    showActionSheetWithOptions(
      {
        options: items.map(x => x.text),
        destructiveButtonIndex: items.findIndex(x => x.destructive),
        cancelButtonIndex: items.findIndex(x => x.type === 'cancel'),
      },
      async buttonIndex => {
        const button = items[buttonIndex ?? -1];
        if (button == null) {
          return;
        }

        if (button.type === 'print-order') {
          // print order
          const url = await orderService.getRenderOrderAsPdfUrl(orderNo);
          await sharePdf(url);
        } else if (button.type === 'sms-share-to-customer') {
          // share via sms
          await shareReportViaSms({
            target: { type: 'task', id: taskNo },
            licensePlateNo,
            orderNo,
            reportNo,
            shareToken,
            mobile: customerMobile ?? undefined,
          });
        } else if (
          button.type === 'wecom-share-to-sa' ||
          button.type === 'wecom-share-to-customer'
        ) {
          await shareReportViaWeWork({
            target: { type: 'task', id: taskNo },
            licensePlateNo,
            orderNo,
            reportNo,
            shareToken,
            shareType: button.type === 'wecom-share-to-sa' ? 'sa' : undefined,
          });
        } else if (
          button.type === 'wechat-share-to-sa' ||
          button.type === 'wechat-share-to-customer'
        ) {
          // weixin sharing
          await shareReportViaWeixin({
            target: { type: 'task', id: taskNo },
            licensePlateNo,
            orderNo,
            reportNo,
            shareToken,
            shareType: button.type === 'wechat-share-to-sa' ? 'sa' : undefined,
          });
        } else if (button.type === 'cancel-order') {
          await cancelOrder();
          onRefresh?.();
        } else if (button.type === 'refresh') {
          onRefresh?.();
        } else if (button.type === 'create-child-order') {
          appNav.navigate('_order', {
            mode: 'new',
            type: 'child',
            mainOrderNo: orderNo,
            onCreated: (order, task) => {
              setTimeout(() => {
                invariant(order.parentOrderNo);
                invariant(task.parentTaskNo);
                if (order.parentOrderNo !== orderNo) {
                  // navigate to the parent task by replacing the current task page.
                  taskNav.replace('Task', {
                    taskNo: task.parentTaskNo,
                    presentedAs: 'replace',
                  });
                }
              }, 1000);
            },
          });
        }
      },
    );
  });

  return { showActions };
};
